import { SingleProcedureRepository, ProcedureId, Procedure } from '@/Domain/Procedures'
import { BatchRepository, Batch } from '@/Domain/Batch'
import { OfferRepository, Offer, OfferTarget } from '@/Domain/Offer'
import { XmlOfferBuilderError, XmlOfferBuilderService } from '@/Domain/XmlOffer/Builder'
import { AccountId } from '@/Domain/Account'
import { UserName, XmlOffer } from '@/Domain/XmlOffer'
import { Species } from '@/Domain/Batch/Species/Species'
import store from '@/Infrastructure/Store'

import { create } from 'xmlbuilder2'
import { Currency } from '@/Domain/Batch/Currency'

export class StandardXmlOfferBuilderService implements XmlOfferBuilderService {
  private _procedureRepository: SingleProcedureRepository
  private _batchRepository: BatchRepository
  private _offerRepository: OfferRepository

  constructor (
    procedureRepository: SingleProcedureRepository,
    batchRepository: BatchRepository,
    offerRepository: OfferRepository
  ) {
    this._procedureRepository = procedureRepository
    this._batchRepository = batchRepository
    this._offerRepository = offerRepository
  }

  async buildXmls (procedureId: ProcedureId, accountId: AccountId, userName: UserName): Promise<XmlOffer[]> {
    const results: XmlOffer[] = []
    const procedure = await this._procedureRepository.byId(procedureId)
    const batches = await this._batchRepository.byProcedureId(procedureId)
    const selectedBatches = this.getSelectedBatches(batches)
    if (selectedBatches !== undefined && selectedBatches.length > 0 && this.checkOffersByTotals(selectedBatches)) {
      selectedBatches.forEach(batch => {
        const batchSection = this.buildBatchSection(batch)
        if (batchSection === null) {
          return
        }

        try {
          // Generate JSON
          const objXml = {
            'sobre-b': {
              ...this.buildProcedureSection(procedure),
              ...this.buildSolicitanteSection(accountId.accountId(), userName.name()),
              ...this.buildBatchSection(batch)
            }
          }

          // Create XML
          const xml = create({ encoding: 'UTF-8' }, objXml).end({ prettyPrint: true })

          // Create Offer
          const offer = new XmlOffer(batch.batchId(), batch.recordId(), xml)

          results.push(offer)
        } catch (ex) { }
      })
    }

    return results
  }

  private getSelectedBatches (batches: Batch[]) {
    const batchesSelected = { ...store.getters['auction/procedureData'] }
    const selectedArray = Object.values(batchesSelected)
    const selectedBatches: Batch[] = []

    batches.forEach(batch => {
      const index = selectedArray.findIndex(sel => sel === batch.batchId().toString())
      if (index > -1) {
        selectedBatches.push(batch)
      }
    })
    return selectedBatches
  }

  // eslint-disable-next-line
  private buildProcedureSection (procedure: Procedure): Record<string, any> {
    const procedureDateParts = procedure.procedureId().split('-')
    const objProcedure = {
      procedemento: {
        codigo: procedure.procedureId(),
        tipo: procedure.procedureType(),
        ano: procedureDateParts[2]
      }
    }

    return objProcedure
  }

  // eslint-disable-next-line
  private buildSolicitanteSection (accountId: string, userName: string): Record<string, any> {
    const objSolicitante = {
      solicitante: {
        'razon-social': userName,
        nif: accountId
      }
    }

    return objSolicitante
  }

  // eslint-disable-next-line
  private buildBatchSection (batch: Batch): Record<string, any> | null {

    try {
      const offer = this._offerRepository.byBatchId(batch.batchId())
      // eslint-disable-next-line
      const especies: Array<Record<string, any>> = []
      batch.species().forEach(specie => {
        especies.push(batch.batchType() === 'RISCO'
          ? this.buildRiscoBatchSection(batch.volumeUnit(), specie)
          : this.buildResultasBatchSection(batch.volumeUnit(), specie, offer))
      })

      const objExpediente = {
        expediente: {
          tipo: batch.batchType(),
          lote: batch.batchId(),
          codigo: batch.recordId(),
          especies: { especie: especies },
          oferta: {
            cantidade: offer.globalAmount(),
            'cantidade-letra': offer.stringifiedAmount()
          }
        }
      }
      return objExpediente
    } catch (error) {
      return null
    }
  }

  private buildRiscoBatchSection (volumeUnit: string, especie: Species) {
    const objRisco = {
      nome: especie.binomen(),
      pes: especie.footNumber(),
      volume: {
        '@unidade': volumeUnit,
        '#text': especie.volume()
      },
      'euros-por-unidade': especie.cost(),
      oferta: '0'
    }

    return objRisco
  }

  private buildResultasBatchSection (volumeUnit: string, especie: Species, offer: Offer) {
    const ofertas = []

    if (offer.checkOffer(especie.internalId(), OfferTarget.SERRA1)) {
      ofertas.push(
        this.buildTargetSection(
          especie.sawOneCost(),
          volumeUnit,
          especie.sawOneVolume(),
          offer.speciesAmount(especie.internalId(), OfferTarget.SERRA1),
          OfferTarget.SERRA1)
      )
    }

    if (offer.checkOffer(especie.internalId(), OfferTarget.SERRA2)) {
      ofertas.push(
        this.buildTargetSection(
          especie.sawTwoCost(),
          volumeUnit,
          especie.sawTwoVolume(),
          offer.speciesAmount(especie.internalId(), OfferTarget.SERRA2),
          OfferTarget.SERRA2)
      )
    }

    if (offer.checkOffer(especie.internalId(), OfferTarget.TRITURACION)) {
      ofertas.push(
        this.buildTargetSection(
          especie.grindedCost(),
          volumeUnit,
          especie.grindedVolume(),
          offer.speciesAmount(especie.internalId(), OfferTarget.TRITURACION),
          OfferTarget.TRITURACION)
      )
    }

    const objResultas = {
      nome: especie.binomen(),
      internalId: especie.internalId(),
      pes: especie.footNumber(),
      volume: {
        '@unidade': volumeUnit,
        '#text': especie.volume()
      },
      ofertas: { oferta: ofertas }
    }

    return objResultas
  }

  private buildTargetSection (
    coste: string,
    volumeUnit: string,
    volumen: string,
    amount: string,
    target: string
    // eslint-disable-next-line
  ): Record<string, any> {
    const objTarget = {
      'euros-por-unidade': coste,
      volume: {
        '@unidade': volumeUnit,
        '#text': volumen
      },
      cantidad: amount,
      destino: target
    }

    return objTarget
  }

  private checkOffersByTotals (batches: Batch[]) {
    let result = true
    batches.forEach(batch => {
      if (batch.batchType() === 'RESUL') {
        const offer = this._offerRepository.byBatchId(batch.batchId())
        const global = offer.globalAmount()
        let totalSpecies = new Currency(0)
        batch.species().forEach(especie => {
          let totalSpecie = new Currency(0)
          if (offer.checkOffer(especie.internalId(), OfferTarget.SERRA1)) {
            const amount1 = offer.speciesAmount(especie.internalId(), OfferTarget.SERRA1)
            const amountCalc1 = parseFloat(amount1.replace('.', '').replace(',', '.'))
            const vol1 = especie.sawOneVolume()
            const volCalc1 = parseFloat(vol1.replace('.', '').replace(',', '.'))
            const offerAmount1 = new Currency(amountCalc1 * volCalc1)
            const roundOfferAmount1 = this.round(offerAmount1, 2)
            totalSpecie = totalSpecie.add(new Currency(roundOfferAmount1))
          }
          if (offer.checkOffer(especie.internalId(), OfferTarget.SERRA2)) {
            const amount2 = offer.speciesAmount(especie.internalId(), OfferTarget.SERRA2)
            const amountCalc2 = parseFloat(amount2.replace('.', '').replace(',', '.'))
            const vol2 = especie.sawTwoVolume()
            const volCalc2 = parseFloat(vol2.replace('.', '').replace(',', '.'))
            const offerAmount2 = new Currency(amountCalc2 * volCalc2)
            const roundOfferAmount2 = this.round(offerAmount2, 2)
            totalSpecie = totalSpecie.add(new Currency(roundOfferAmount2))
          }
          if (offer.checkOffer(especie.internalId(), OfferTarget.TRITURACION)) {
            const amountTri = offer.speciesAmount(especie.internalId(), OfferTarget.TRITURACION)
            const amountCalcTri = parseFloat(amountTri.replace('.', '').replace(',', '.'))
            const volTri = especie.grindedVolume()
            const volCalcTri = parseFloat(volTri.replace('.', '').replace(',', '.'))
            const offerAmountTri = new Currency(amountCalcTri * volCalcTri)
            const roundOfferAmountTri = this.round(offerAmountTri, 2)
            totalSpecie = totalSpecie.add(new Currency(roundOfferAmountTri))
          }
          totalSpecies = totalSpecies.add(totalSpecie)
        })

        if (global.trim() !== totalSpecies.toString().trim()) {
          result = false
          throw new XmlOfferBuilderError('Datos corruptos')
        }
      }
    })
    return result
  }

  private round (numberCurrency:Currency, decimals: number) {
    const numeroRegexp = new RegExp('\\d\\.(\\d){' + decimals + ',}')
    const number = numberCurrency.value()
    if (numeroRegexp.test(number.toString())) {
      return Number(number.toFixed(decimals))
    }
    return Number(number.toFixed(decimals)) === 0 ? 0 : number
  }
}
